import { useState } from "react";
import { CaretLeft, CaretRight, ChatsCircle, Newspaper, X } from "@phosphor-icons/react";
import { api, act, useFetch } from "@/lib/api";
import { Panel, Badge, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { DayTimePicker, fromISO, toISO } from "@/components/studio/DayTimePicker";

const monday = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const fmt = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
const Icon = ({ k }) => (k === "social" ? <ChatsCircle size={14} /> : <Newspaper size={14} />);

const TONE = { scheduled: "blue", not_published: "red", awaiting_approval: "amber", skipped: "red" };
const Entry = ({ it, onUnschedule }) => (
  <div className="border border-border rounded-md p-2 bg-black text-xs space-y-1 rise" data-testid={`cal-item-${it.id}`}>
    {it.thumb && <img src={it.thumb} alt="" className="w-full h-16 object-cover rounded-sm" />}
    <div className="flex items-center gap-1 text-zinc-400 font-mono"><Icon k={it.kind} />{new Date(it.at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</div>
    <div className="line-clamp-2 text-zinc-200">{it.title}</div>
    <div className="flex items-center gap-1"><Badge v={it.status} tone={TONE[it.status] || "green"} data-testid={`cal-status-${it.id}`} />
      {it.status === "scheduled" && <button className="ml-auto text-zinc-500 hover:text-white" data-testid={`cal-unschedule-${it.id}`} onClick={() => onUnschedule(it)}><X size={12} /></button>}</div>
  </div>
);

const Ready = ({ it, onSchedule }) => {
  const [v, setV] = useState({ day: null, time: "09:00" });
  const { data: bt } = useFetch(it.kind === "newsletter" ? "/studio/best-times?kind=newsletter" : `/studio/best-times?networks=${(it.networks || []).join(",")}`);
  const pick = (iso) => setV(fromISO(iso));
  return (
    <div className="py-2" data-testid={`cal-ready-${it.id}`}>
    <div className="flex items-center gap-2">
      <Icon k={it.kind} /><span className="text-sm flex-1 truncate">{it.title}</span>
      <DayTimePicker value={v} onChange={setV} testid={`cal-${it.id}`} />
      <Button size="sm" disabled={!v.day || !v.time} data-testid={`cal-schedule-${it.id}`} onClick={() => onSchedule(it, toISO(v))} className="h-8 bg-[#007AFF] hover:bg-[#3395FF]">Schedule</Button>
    </div>
    {bt?.slots?.length > 0 && <div className="flex items-center gap-1.5 mt-1.5 pl-6 flex-wrap" title={bt.basis}>
      <span className="text-[11px] text-zinc-500">Best times:</span>
      {bt.slots.map((s, i) => <button key={s.at} type="button" data-testid={`cal-suggest-${it.id}-${i}`} onClick={() => pick(s.at)} className="font-mono text-[11px] px-2 py-0.5 rounded-full border border-[#007AFF]/50 text-[#7FB8FF] hover:bg-[#007AFF]/10">{s.label}{i === 0 && " ★"}</button>)}
      <span className="text-[10px] text-zinc-600 truncate">{bt.basis}</span>
    </div>}
    </div>
  );
};

export const ContentCalendar = () => {
  const [start, setStart] = useState(monday(new Date()));
  const { data, reload } = useFetch(`/studio/calendar?start=${encodeURIComponent(start.toISOString())}&days=7`);
  const shift = (n) => setStart(new Date(start.getTime() + n * 7 * 86400000));
  const sched = async (it, at) => { await act(() => api.post(`/studio/${it.kind}/${it.id}/schedule`, { at }), at ? "Scheduled — it goes out automatically" : "Unscheduled"); reload(); };
  const days = [...Array(7)].map((_, i) => new Date(start.getTime() + i * 86400000));
  const today = new Date().toDateString();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="secondary" data-testid="cal-prev" onClick={() => shift(-1)}><CaretLeft /></Button>
        <Button size="icon" variant="secondary" data-testid="cal-next" onClick={() => shift(1)}><CaretRight /></Button>
        <Button size="sm" variant="ghost" data-testid="cal-today" onClick={() => setStart(monday(new Date()))}>This week</Button>
        <span className="font-mono text-sm text-zinc-300" data-testid="cal-range">{fmt(days[0])} – {fmt(days[6])}</span>
        <span className="text-[11px] text-zinc-500 ml-auto">Checked every 30 min · social goes to connected networks, newsletters follow delivery mode</span>
      </div>
      <div className="grid grid-cols-7 gap-2" data-testid="cal-grid">{days.map((d) => {
        const its = (data?.items || []).filter((x) => new Date(x.at).toDateString() === d.toDateString());
        return (
          <div key={d.toISOString()} className={`min-h-[220px] rounded-md border p-2 space-y-2 ${d.toDateString() === today ? "border-[#007AFF]/60 bg-[#007AFF]/5" : "border-border bg-[#121212]"}`}>
            <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}</div>
            {its.map((it) => <Entry key={it.id} it={it} onUnschedule={(x) => sched(x, null)} />)}
          </div>
        );
      })}</div>
      <Panel title="Approved & ready to schedule">
        {!data?.unscheduled?.length ? <Empty>Approve a social post or newsletter to schedule it here.</Empty>
          : <div className="divide-y divide-border/60">{data.unscheduled.map((it) => <Ready key={it.id} it={it} onSchedule={sched} />)}</div>}
      </Panel>
    </div>
  );
};
